import React, { useMemo } from 'react';
import { Button, DatePicker, Input } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import type { TimeRangePickerProps } from 'antd';
const { RangePicker } = DatePicker;
const { Search } = Input;

type PresetConfig =
  | { key: 'now-eod'; label: string }
  | { key: 'last-7-days' | 'last-14-days' | 'last-30-days' | 'last-90-days'; label: string; days: number };

const presetConfigs: PresetConfig[] = [
  { key: 'now-eod', label: 'Now ~ EOD' },
  { key: 'last-7-days', label: 'Last 7 Days', days: 7 },
  { key: 'last-14-days', label: 'Last 14 Days', days: 14 },
  { key: 'last-30-days', label: 'Last 30 Days', days: 30 },
  { key: 'last-90-days', label: 'Last 90 Days', days: 90 },
];

const getPresetRange = (key: string) => {
  if (key === 'now-eod') {
    return [dayjs(), dayjs().endOf('day')] as [Dayjs, Dayjs];
  }

  const preset = presetConfigs.find((config): config is Extract<PresetConfig, { days: number }> => config.key === key && 'days' in config);
  if (!preset) return null;

  return [dayjs().subtract(preset.days, 'd'), dayjs()] as [Dayjs, Dayjs];
};

const getMatchingPresetKey = (startDate: Dayjs | null, endDate: Dayjs | null) => {
  if (!startDate || !endDate) return null;

  if (endDate.isSame(startDate.endOf('day'), 'second')) {
    return 'now-eod';
  }

  const diffInMinutes = endDate.diff(startDate, 'minute');
  const matchingPreset = presetConfigs.find(
    (config): config is Extract<PresetConfig, { days: number }> => 'days' in config && diffInMinutes === config.days * 24 * 60
  );

  return matchingPreset?.key ?? null;
};

const getPresetLabel = (label: string, isActive: boolean) => (
  <span className={`block rounded px-2 py-1 transition-colors ${isActive ? 'bg-primaryLite font-semibold text-primary' : 'text-gray-700'}`}>
    {label}
  </span>
);

function DashboardHeaderActions({
  onRangeChange,
  onSearch,
  onApply,
  onClear,
  onExport,
  onChange,
  searchTerm,
  dateRangeFilter,
  isApplyDisabled,
}: {
  onRangeChange: (dates: null | (Dayjs | null)[], dateStrings: string[]) => void;
  onSearch: (value: string) => void;
  onApply: () => void;
  onClear: () => void;
  onExport: () => void;
  onChange: (value: any) => void;
  searchTerm: string;
  dateRangeFilter: { startDate: Dayjs | null; endDate: Dayjs | null };
  isApplyDisabled: boolean;
}) {
  console.log('dateRangeFilter', dateRangeFilter);
  const activePresetKey = useMemo(
    () => getMatchingPresetKey(dateRangeFilter.startDate, dateRangeFilter.endDate),
    [dateRangeFilter.startDate, dateRangeFilter.endDate]
  );

  const presets: TimeRangePickerProps['presets'] = useMemo(
    () =>
      presetConfigs.map(config => ({
        label: getPresetLabel(config.label, activePresetKey === config.key),
        value: () => getPresetRange(config.key) ?? [dayjs(), dayjs()],
      })),
    [activePresetKey]
  );

  const disableFutureDates = (current: Dayjs) => current.isAfter(dayjs().endOf('day'));

  return (
    <>
      <Search placeholder="Search" onSearch={onSearch} onChange={onChange} style={{ width: 200 }} value={searchTerm} />
      <RangePicker
        showTime
        size="middle"
        presets={presets}
        onChange={onRangeChange}
        format="YYYY/MM/DD HH:mm:ss"
        disabledDate={disableFutureDates}
        value={[dateRangeFilter.startDate, dateRangeFilter.endDate]} // controlled value
      />
      <Button
        color="default"
        variant="solid"
        onClick={onApply}
        className={`${isApplyDisabled ? 'bg-gray-400 text-gray-100 cursor-not-allowed' : 'bg-gray-300 text-gray-800 hover:bg-gray-400'}`}
        disabled={isApplyDisabled}>
        Apply
      </Button>
      <Button color="default" onClick={onClear}>
        Clear
      </Button>
      <div className="w-[1px] h-8 bg-gray-300 m-2"></div>
      <Button color="default" onClick={onExport}>
        Export excel
      </Button>
    </>
  );
}

export default DashboardHeaderActions;
